/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Spin,
  DatePicker,
  Checkbox,
  InputNumber,
  type CheckboxProps,
  type DatePickerProps,
} from 'antd';
import ImageUpload from '../../../components/ImageUpload';
import WebcamImage from '../../../components/ImageUpload/webCamUpload';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  createNewCustomerAction,
  updateCustomerAction,
  getCustomerAgentsByBranchAction,
} from '../store/customerActions';
import { getBankNameAction } from '../../InterestScheme/store/interestSchemeActions';
import dayjs, { type Dayjs } from 'dayjs';
import { type Customer, type BankBranchLimit } from '../store/customerTypes';
import { type AppDispatch, type RootState } from '../../../redux/store';
import { type BankName } from '../../InterestScheme/store/interestSchemeTypes';

interface Props {
  customer?: Customer;
}

// const TYPE_LABELS: Record<string, string> = {
//   customer: 'Customer',
//   agent: 'Agent',
//   bankAccountName: 'Bank Account Name',
// };

const NAME_LABELS: Record<string, string> = {
  customer: 'Customer',
  agent: 'Agent',
  bankAccountName: 'Account',
};

export default function CustomerFormIndex({ customer }: Props): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [showWebCam, setShowWebCam] = useState(false);
  const handleWebCam = (): void => { setShowWebCam(!showWebCam); };

  const [showWebCam1, setShowWebCam1] = useState(false);
  const handleWebCam1 = (): void => { setShowWebCam1(!showWebCam1); };

  const [form] = Form.useForm();

  const [branchOptions, setBranchOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [agentOptions, setAgentOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [bankOptions, setBankOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  const { banks }: any = useSelector<RootState>((state: any) => state.interestScheme);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customerType, setCustomerType] = useState<'customer' | 'agent' | 'bankAccountName'>('customer');
  const [editCustomerId, setEditCustomerId] = useState<string | null | undefined>(null);
  const [customerName, setCustomerName] = useState<string>('');
  const [aadharNumber, setAadharNumber] = useState<string>('');
  const [branch, setBranch] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [alternatePhoneNumber, setAlternatePhoneNumber] = useState<string | null>(null);
  const [drivingLicense, setDrivingLicense] = useState<string>('');
  const [panNumber, setPanNumber] = useState<string>('');
  const [datePickerDate, setDatePickerDate] = useState<Dayjs | null>(null);
  const [nomineeName, setNomineeName] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [photo, setPhoto] = useState<string>('');
  const [proofPhoto, setProofPhoto] = useState<string>('');
  const [mail, setMail] = useState<string>('');
  const [permanentAddress, setPermanentAddress] = useState<string>('');
  const [fatherName, setFatherName] = useState<string>('');
  const [area, setArea] = useState<string>('');
  const [pincode, setPincode] = useState<string>('');
  const [selectedAgent, setSelectedAgent] = useState<string | undefined>(undefined);
  const [savedAgent, setSavedAgent] = useState<string | undefined>(undefined);
  const [bankBranches, setBankBranches] = useState<BankBranchLimit[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);

  const emptyBankBranch = (): BankBranchLimit => ({
    bank: '',
    branch: '',
    maxAmountLimit: 0,
    pledgeLimit: 0,
    minimumBalancePaid: 0,
  });

  const updateBankBranch = (idx: number, patch: Partial<BankBranchLimit>): BankBranchLimit[] => {
    const updated = bankBranches.map((bb, i) => (i === idx ? { ...bb, ...patch } : bb));
    setBankBranches(updated);
    return updated;
  };

  const onChangeCheckbox: CheckboxProps['onChange'] = (e) => {
    if (e.target.checked) {
      setPermanentAddress(address);
      form.setFieldsValue({ permanentAddress: address });
    }
  };

  const loadAgentsForBranches = async (branchIds: string[]) => {
    if (!branchIds.length) { setAgentOptions([]); return; }
    setAgentsLoading(true);
    const results = await Promise.all(branchIds.map(async id => await getCustomerAgentsByBranchAction(dispatch, { branch: id })));
    setAgentsLoading(false);
    const seen = new Set<string>();
    const merged = results.flat().filter((a: { _id: string; customerId: string; name: string }) => {
      if (seen.has(a._id)) return false;
      seen.add(a._id);
      return true;
    });
    setAgentOptions(merged.map((a: { _id: string; customerId: string; name: string }) => ({ label: `${a.customerId} - ${a.name}`, value: a._id })));
  };

  useEffect(() => {
    if (customer != null) {
      form.setFieldsValue(customer);
      setEditCustomerId(customer._id);
      setCustomerType(customer.type ?? 'customer');
      setCustomerName(customer.name);
      setAadharNumber(customer.aadhar);
      setBranch(customer.branch);
      setPhoneNumber(customer.phoneNumber);
      setAlternatePhoneNumber(customer.alternatePhoneNumber);
      setDrivingLicense(customer.drivingLicenseNumber);
      setPanNumber(String(customer.pan ?? ''));
      setDatePickerDate(dayjs(customer.dob));
      setNomineeName(customer.nominee);
      setAddress(customer.address);
      setPhoto(customer.photo);
      setProofPhoto(customer.proofPhoto);
      setMail(customer.mail);
      setPermanentAddress(customer.permanentAddress);
      setFatherName(customer.fatherName);
      setArea(customer.area);
      setPincode(customer.pincode);
      const agentValue = customer.agent != null ? String((customer.agent as any)?._id ?? customer.agent) : undefined;
      setSelectedAgent(agentValue);
      setSavedAgent(agentValue);
      if (customer.type === 'bankAccountName') {
        void getBankNameAction(dispatch, {});
        // accounts created before limits were split per combination carry a single
        // account-wide value, which only maps cleanly onto a lone combination
        const legacyMax = typeof customer.maxAmountLimit === 'number' ? customer.maxAmountLimit : 0;
        const legacyPledge = typeof customer.pledgeLimit === 'number' ? customer.pledgeLimit : 0;
        const singleCombination = (customer.bankBranches?.length ?? 0) <= 1;
        const existingBankBranches: BankBranchLimit[] = customer.bankBranches?.length
          ? customer.bankBranches.map((bb: any): BankBranchLimit => ({
              bank: String(bb.bank?._id ?? bb.bank ?? ''),
              branch: String(bb.branch?._id ?? bb.branch ?? ''),
              maxAmountLimit: bb.maxAmountLimit ?? (singleCombination ? legacyMax : 0),
              pledgeLimit: bb.pledgeLimit ?? (singleCombination ? legacyPledge : 0),
              minimumBalancePaid: bb.minimumBalancePaid ?? 0,
            }))
          : customer.bank
          ? [{
              bank: String((customer.bank as any)?._id ?? customer.bank),
              branch: String((customer as any).branch?._id ?? (customer as any).branch ?? ''),
              maxAmountLimit: legacyMax,
              pledgeLimit: legacyPledge,
              minimumBalancePaid: 0,
            }]
          : [emptyBankBranch()];
        setBankBranches(existingBankBranches);
        const uniqueBranchIds = Array.from(new Set(existingBankBranches.map(bb => bb.branch).filter((b): b is string => Boolean(b))));
        if (uniqueBranchIds.length) void loadAgentsForBranches(uniqueBranchIds);
      }
      if (customer.type === 'agent') {
        const existingBranches = customer.branches?.length
          ? customer.branches
          : customer.branch
          ? [customer.branch]
          : [];
        setSelectedBranches(existingBranches);
      }
    } else {
      form.resetFields();
      setEditCustomerId(null);
      setCustomerType('customer');
      setCustomerName('');
      setAadharNumber('');
      setBranch('');
      setPhoneNumber('');
      setAlternatePhoneNumber('');
      setDrivingLicense('');
      setPanNumber('');
      setDatePickerDate(null);
      setNomineeName('');
      setAddress('');
      setPhoto('');
      setProofPhoto('');
      setMail('');
      setPermanentAddress('');
      setFatherName('');
      setArea('');
      setPincode('');
      setSelectedAgent(undefined);
      setSavedAgent(undefined);
      setBankBranches([]);
      setSelectedBranches([]);
      setAgentOptions([]);
    }
  }, [customer, form]);

  useEffect(() => {
    const branches = localStorage.getItem('branches');
    if (branches != null) {
      const parsedBranches = JSON.parse(branches);
      const branchOptionTemp: Array<{ label: string; value: string }> =
        parsedBranches.map((b: { code: string; id: string }) => ({
          label: b.code,
          value: b.id,
        }));
      setBranchOptions(branchOptionTemp);
      if (!customer) {
        setBranch(branchOptionTemp[0].value);
        form.setFieldsValue({ branch: branchOptionTemp[0].value });
      }
    }
  }, [customer, form]);

  useEffect(() => {
    if (banks && (banks as BankName[]).length > 0) {
      setBankOptions((banks as BankName[]).map((b): { label: string; value: string } => ({ label: b.name, value: String(b._id) })));
    }
  }, [banks]);

  const customValidator = () => {
    return !(form.getFieldValue('aadhar') || form.getFieldValue('drivingLicenseNumber'));
  };

  const handleBranchChange = (value: string) => {
    form.setFieldsValue({ branch: value, agent: undefined });
    setBranch(value);
    setSelectedAgent(undefined);
    if (customerType === 'bankAccountName') {
      const bankBranchIds = bankBranches.map(bb => bb.branch).filter(Boolean);
      void loadAgentsForBranches(Array.from(new Set([value, ...bankBranchIds])));
    }
  };

  const handleTypeChange = (value: 'customer' | 'agent' | 'bankAccountName') => {
    setCustomerType(value);
    form.setFieldsValue({ type: value, agent: undefined });
    setSelectedAgent(undefined);
    setBankBranches([]);
    setSelectedBranches([]);
    setAgentOptions([]);
    if (value === 'bankAccountName') {
      void getBankNameAction(dispatch, {});
      if (branch) void loadAgentsForBranches([branch]);
    }
  };

  const onFinish = async (values: Customer) => {
    setIsLoading(true);
    const action = customer != null ? updateCustomerAction : createNewCustomerAction;
    const response = await action(dispatch, {
      ...values,
      type: customerType,
      photo,
      ...(editCustomerId != null && { customerEditId: editCustomerId }),
      ...(customerType === 'bankAccountName' && {
        bankBranches: bankBranches.map((bb) => ({
          ...bb,
          maxAmountLimit: bb.maxAmountLimit ?? 0,
          pledgeLimit: bb.pledgeLimit ?? 0,
          minimumBalancePaid: bb.minimumBalancePaid ?? 0,
        })),
        agent: selectedAgent ?? null,
      }),
      ...(customerType === 'agent' && {
        branches: selectedBranches,
      }),
    });
    setIsLoading(false);
    if (response && editCustomerId == null) {
      form.resetFields();
      setCustomerType('customer');
      setCustomerName('');
      setAadharNumber('');
      setBranch(branchOptions[0].value);
      setPhoneNumber('');
      setDrivingLicense('');
      setPanNumber('');
      setMail('');
      setNomineeName('');
      setAddress('');
      setDatePickerDate(null);
      setPhoto('');
      setProofPhoto('');
      setPermanentAddress('');
      setBankBranches([]);
      setSelectedBranches([]);
      setAgentOptions([]);
      navigate(`/customer?customerId=${response}&tab=3`);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const onChangeDate: DatePickerProps['onChange'] = (date: Dayjs | null, dateString: string): void => {
    form.setFieldsValue({ dob: dateString });
    setDatePickerDate(date);
  };

  const disabledDate = (current: Dayjs) => current > dayjs().startOf('day');

  // const typeLabel = TYPE_LABELS[customerType] ?? 'Customer';
  const nameLabel = NAME_LABELS[customerType] ?? 'Customer';

  return (
    <Form
      form={form}
      name="customer_form"
      initialValues={customer}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Spin spinning={isLoading} fullscreen />
      {customer?.customerId != null && (
        <p style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>{customer.customerId}</p>
      )}
      <div className="customers__form">
        <div className="customers__form__input">

          {/* Row 1: Type + Name side by side */}
          <div style={{ display: 'flex', flexDirection: 'row', gap: 32, alignItems: 'flex-start' }}>
            <Form.Item name="type" style={{ marginBottom: 0, flexShrink: 0 }}>
              <div className="customers__input__container">
                <p className="customers__input__label">Type</p>
                <Select
                  style={{ width: 311 }}
                  value={customerType}
                  disabled={!!editCustomerId}
                  onChange={handleTypeChange}
                  options={[
                    { label: 'Customer', value: 'customer' },
                    { label: 'Agent', value: 'agent' },
                    { label: 'Bank Account Name', value: 'bankAccountName' },
                  ]}
                  className="customers__select"
                />
              </div>
            </Form.Item>

            <Form.Item
              name="name"
              style={{ marginBottom: 0, flexShrink: 0 }}
              rules={[{ required: true, message: `Please input ${nameLabel} name!` }]}
            >
              <div className="customers__input__container">
                <p className="customers__input__label">{nameLabel} Name</p>
                <Input
                  value={customerName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    form.setFieldsValue({ name: e?.target?.value });
                    setCustomerName(e?.target?.value);
                  }}
                  className="customers__input"
                  placeholder={`Enter ${nameLabel} Name`}
                />
              </div>
            </Form.Item>
          </div>

          {/* Row 2: Both images side by side */}
          <div style={{ display: 'flex', flexDirection: 'row', gap: 32, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <Form.Item
                name="photo"
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: 'Please upload profile photo!' }]}
              >
                <div className="customers__photo__container">
                  <p className="customers__input__label">Profile Image</p>
                  <p className="customers__photo__sub__title">
                    Image format .jpg .jpeg .png. Minimum size 300px x 300px
                  </p>
                  <ImageUpload
                    onImage={(image: string) => {
                      form.setFieldsValue({ photo: image });
                      setPhoto(image);
                      setShowWebCam(false);
                    }}
                    src={photo}
                  />
                  {!showWebCam && (
                    <p className="customers__button" onClick={handleWebCam}>Start Webcam</p>
                  )}
                  {showWebCam && (
                    <WebcamImage
                      name="photo"
                      onChange={(e: { target: { name: string; value: string } }) => {
                        form.setFieldsValue({ photo: e?.target?.value });
                        setPhoto(e?.target?.value);
                      }}
                    />
                  )}
                </div>
              </Form.Item>
            </div>

            <div style={{ flex: 1, minWidth: 280 }}>
              <Form.Item
                name="proofPhoto"
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: 'Please upload proof photo!' }]}
              >
                <div className="customers__photo__container">
                  <p className="customers__input__label">Proof Image</p>
                  <p className="customers__photo__sub__title">
                    Image format .jpg .jpeg .png. Minimum size 300px x 300px
                  </p>
                  <ImageUpload
                    onImage={(image: string) => {
                      form.setFieldsValue({ proofPhoto: image });
                      setProofPhoto(image);
                      setShowWebCam1(false);
                    }}
                    src={proofPhoto}
                  />
                  {!showWebCam1 && (
                    <p className="customers__button" onClick={handleWebCam1}>Start Webcam</p>
                  )}
                  {showWebCam1 && (
                    <WebcamImage
                      name="photo"
                      onChange={(e: { target: { name: string; value: string } }) => {
                        form.setFieldsValue({ proofPhoto: e?.target?.value });
                        setProofPhoto(e?.target?.value);
                      }}
                    />
                  )}
                </div>
              </Form.Item>
            </div>
          </div>

          {/* Rows 3+: Regular fields in wrapping flex row */}
          <div className="customers__form__fields" style={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 64}}>
            <div>
              <Form.Item
                name="dob"
                rules={[{ required: true, message: 'Please select date of birth!' }]}
              >
                <div className="customers__input__container">
                  <p className="customers__input__label">Date of Birth</p>
                  <DatePicker
                    value={datePickerDate}
                    disabledDate={disabledDate}
                    className="customers__input"
                    onChange={onChangeDate}
                  />
                </div>
              </Form.Item>
            </div>

            <div>
              <Form.Item
                name="aadhar"
                rules={[{ required: customValidator(), message: 'Aadhar or Driving License required!' }]}
              >
                <div className="customers__input__container">
                  <p className="customers__input__label">Aadhar Number</p>
                  <Input
                    value={aadharNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      form.setFieldsValue({ aadhar: e?.target?.value });
                      setAadharNumber(e?.target?.value);
                    }}
                    maxLength={12}
                    className="customers__input"
                    placeholder="Enter Aadhar Number"
                  />
                </div>
              </Form.Item>
            </div>

            <div>
              <Form.Item
                name="drivingLicenseNumber"
                rules={[{ required: customValidator(), message: 'Aadhar or Driving License required!' }]}
              >
                <div className="customers__input__container">
                  <p className="customers__input__label">Driving License</p>
                  <Input
                    value={drivingLicense}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      form.setFieldsValue({ drivingLicenseNumber: e?.target?.value });
                      setDrivingLicense(e?.target?.value);
                    }}
                    maxLength={15}
                    className="customers__input"
                    placeholder="Enter Driving License"
                  />
                </div>
              </Form.Item>
            </div>

            <div>
              <Form.Item
                name="pan"
                rules={[
                  {
                    pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
                    message: 'Enter a valid PAN (e.g. ABCDE1234F)',
                  },
                ]}
              >
                <div className="customers__input__container">
                  <p className="customers__input__label">PAN Number</p>
                  <Input
                    value={panNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e?.target?.value.toUpperCase();
                      form.setFieldsValue({ pan: value });
                      setPanNumber(value);
                    }}
                    maxLength={10}
                    className="customers__input"
                    placeholder="Enter PAN Number"
                  />
                </div>
              </Form.Item>
            </div>

            <div>
              <Form.Item
                name="phoneNumber"
                rules={[{ required: true, message: 'Please input phone number!' }]}
              >
                <div className="customers__input__container">
                  <p className="customers__input__label">Phone Number</p>
                  <Input
                    value={phoneNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      form.setFieldsValue({ phoneNumber: e?.target?.value });
                      setPhoneNumber(e?.target?.value);
                    }}
                    maxLength={10}
                    className="customers__input"
                    placeholder="Enter Phone Number"
                  />
                </div>
              </Form.Item>
            </div>

            <div>
              <Form.Item
                name="alternatePhoneNumber"
                rules={[{ required: true, message: 'Please input phone number!' }]}
              >
                <div className="customers__input__container">
                  <p className="customers__input__label">Alternate Phone Number</p>
                  <Input
                    value={alternatePhoneNumber ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      form.setFieldsValue({ alternatePhoneNumber: e?.target?.value });
                      setAlternatePhoneNumber(e?.target?.value);
                    }}
                    maxLength={10}
                    className="customers__input"
                    placeholder="Enter Phone Number"
                  />
                </div>
              </Form.Item>
            </div>

            <div>
              <Form.Item name="mail">
                <div className="customers__input__container">
                  <p className="customers__input__label">Email</p>
                  <Input
                    value={mail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      form.setFieldsValue({ mail: e?.target?.value });
                      setMail(e?.target?.value);
                    }}
                    type="email"
                    className="customers__input"
                    placeholder="Enter email"
                  />
                </div>
              </Form.Item>
            </div>

            {!editCustomerId && customerType === 'customer' && (
              <div>
                <Form.Item
                  name="branch"
                  rules={[{ required: true, message: 'Please select branch!' }]}
                >
                  <div className="customers__input__container">
                    <p className="customers__input__label">Branch</p>
                    <Select
                      style={{ width: 311 }}
                      placeholder="Select branch"
                      value={branch}
                      onChange={handleBranchChange}
                      options={branchOptions}
                      className="customers__select"
                    />
                  </div>
                </Form.Item>
              </div>
            )}

            {customerType === 'bankAccountName' && (
              <>
                {/* Bank + Branch pairs — limits are tracked per combination */}
                <div style={{ width: '100%', flexBasis: '100%' }}>
                  <p className="customers__input__label" style={{ marginBottom: 8 }}>Bank / Branch</p>
                  {bankBranches.map((pair, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-end',
                        flexWrap: 'wrap',
                        marginBottom: 12,
                        padding: 12,
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                      }}
                    >
                      <div>
                        <p className="customers__input__label">Bank</p>
                        <Select
                          style={{ width: 180 }}
                          placeholder="Select bank"
                          value={pair.bank || undefined}
                          onChange={(value: string) => {
                            updateBankBranch(idx, { bank: value });
                          }}
                          options={bankOptions}
                          className="customers__select"
                        />
                      </div>
                      <div>
                        <p className="customers__input__label">Branch</p>
                        <Select
                          style={{ width: 180 }}
                          placeholder="Select branch"
                          value={pair.branch || undefined}
                          onChange={(value: string) => {
                            const updated = updateBankBranch(idx, { branch: value });
                            const allBranchIds = updated.map((bb) => bb.branch).filter(Boolean);
                            void loadAgentsForBranches(Array.from(new Set(allBranchIds)));
                          }}
                          options={branchOptions}
                          className="customers__select"
                        />
                      </div>
                      <div>
                        <p className="customers__input__label">Max Amount Limit (₹)</p>
                        <InputNumber
                          style={{ width: 180 }}
                          placeholder="Enter max amount limit"
                          value={pair.maxAmountLimit ?? 0}
                          min={0}
                          precision={0}
                          className="customers__input"
                          onChange={(value) => {
                            updateBankBranch(idx, { maxAmountLimit: value ?? 0 });
                          }}
                        />
                      </div>
                      <div>
                        <p className="customers__input__label">Pledge Limit</p>
                        <InputNumber
                          style={{ width: 150 }}
                          placeholder="Enter pledge limit"
                          value={pair.pledgeLimit ?? 0}
                          min={0}
                          precision={0}
                          className="customers__input"
                          onChange={(value) => {
                            updateBankBranch(idx, { pledgeLimit: value ?? 0 });
                          }}
                        />
                      </div>
                      <div>
                        <p className="customers__input__label">Minimum Balance Paid (₹)</p>
                        <InputNumber
                          style={{ width: 180 }}
                          placeholder="Enter minimum balance paid"
                          value={pair.minimumBalancePaid ?? 0}
                          min={0}
                          className="customers__input"
                          onChange={(value) => {
                            updateBankBranch(idx, { minimumBalancePaid: value ?? 0 });
                          }}
                        />
                      </div>
                      <Button
                        type="text"
                        danger
                        onClick={() => {
                          const updated = bankBranches.filter((_, i) => i !== idx);
                          setBankBranches(updated);
                          const allBranchIds = updated.map((bb) => bb.branch).filter(Boolean);
                          void loadAgentsForBranches(Array.from(new Set(allBranchIds)));
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => { setBankBranches([...bankBranches, emptyBankBranch()]); }}
                    style={{ marginBottom: 16 }}
                  >
                    + Add Bank / Branch
                  </Button>
                </div>

                {/* Agent */}
                <div>
                  <Form.Item name="agent">
                    <div className="customers__input__container">
                      <p className="customers__input__label">Agent (Optional)</p>
                      <Select
                        style={{ width: 311 }}
                        placeholder="Select agent"
                        value={selectedAgent}
                        disabled={!!editCustomerId && !!savedAgent}
                        onChange={(value: string) => {
                          form.setFieldsValue({ agent: value });
                          setSelectedAgent(value);
                        }}
                        onClear={() => {
                          form.setFieldsValue({ agent: undefined });
                          setSelectedAgent(undefined);
                        }}
                        options={agentOptions}
                        allowClear
                        loading={agentsLoading}
                        notFoundContent={agentsLoading ? <Spin size="small" /> : 'No agents found for selected branches'}
                        className="customers__select"
                      />
                    </div>
                  </Form.Item>
                </div>

              </>
            )}

            {customerType === 'agent' && (
              <div style={{ width: '100%', flexBasis: '100%' }}>
                <p className="customers__input__label" style={{ marginBottom: 8 }}>Associated Branches</p>
                {selectedBranches.map((branchId, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                    <Select
                      style={{ width: 200 }}
                      placeholder="Select branch"
                      value={branchId || undefined}
                      onChange={(value: string) => {
                        const updated = selectedBranches.map((b, i) => i === idx ? value : b);
                        setSelectedBranches(updated);
                      }}
                      options={branchOptions}
                      className="customers__select"
                    />
                    <Button
                      type="text"
                      danger
                      onClick={() => { setSelectedBranches(selectedBranches.filter((_, i) => i !== idx)); }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => { setSelectedBranches([...selectedBranches, '']); }}
                  style={{ marginBottom: 16 }}
                >
                  + Add Branch
                </Button>
              </div>
            )}

            <div>
              <Form.Item
                name="nominee"
                rules={[{ required: true, message: 'Please input nominee name!' }]}
              >
                <div className="customers__input__container">
                  <p className="customers__input__label">Nominee Name</p>
                  <Input
                    value={nomineeName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      form.setFieldsValue({ nominee: e?.target?.value });
                      setNomineeName(e?.target?.value);
                    }}
                    className="customers__input"
                    placeholder="Enter Nominee Name"
                  />
                </div>
              </Form.Item>
            </div>

            <div>
              <Form.Item
                name="fatherName"
                rules={[{ required: true, message: 'Please input father name!' }]}
              >
                <div className="customers__input__container">
                  <p className="customers__input__label">Father Name</p>
                  <Input
                    value={fatherName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      form.setFieldsValue({ fatherName: e?.target?.value });
                      setFatherName(e?.target?.value);
                    }}
                    className="customers__input"
                    placeholder="Enter Father Name"
                  />
                </div>
              </Form.Item>
            </div>

            <div>
              <Form.Item name="area">
                <div className="customers__input__container">
                  <p className="customers__input__label">Area</p>
                  <Input
                    value={area}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      form.setFieldsValue({ area: e?.target?.value });
                      setArea(e?.target?.value);
                    }}
                    className="customers__input"
                    placeholder="Enter Area"
                  />
                </div>
              </Form.Item>
            </div>

            <div>
              <Form.Item
                name="pincode"
                rules={[{ required: true, message: 'Please input pincode!' }]}
              >
                <div className="customers__input__container">
                  <p className="customers__input__label">Pincode</p>
                  <Input
                    value={pincode}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      form.setFieldsValue({ pincode: e?.target?.value });
                      setPincode(e?.target?.value);
                    }}
                    className="customers__input"
                    placeholder="Enter Pincode"
                  />
                </div>
              </Form.Item>
            </div>
          </div>

          {/* Current Address — own row */}
          <Form.Item
            name="address"
            rules={[{ required: true, message: 'Please input address!' }]}
          >
            <div className="customers__input__container">
              <p className="customers__input__label">Current Address</p>
              <Input
                value={address}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  form.setFieldsValue({ address: e?.target?.value });
                  setAddress(e?.target?.value);
                }}
                className="customers__input__address"
                placeholder="Enter current address"
              />
            </div>
          </Form.Item>

          {/* Permanent Address — own row */}
          <div>
            <Form.Item
              name="permanentAddress"
              rules={[{ required: true, message: 'Please input permanent address!' }]}
            >
              <div className="customers__input__container">
                <p className="customers__input__label">Permanent Address</p>
                <Input
                  value={permanentAddress}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    form.setFieldsValue({ permanentAddress: e?.target?.value });
                    setPermanentAddress(e?.target?.value);
                  }}
                  className="customers__input__address"
                  placeholder="Enter permanent address"
                />
              </div>
            </Form.Item>
            <Checkbox
              checked={address !== '' && address === permanentAddress}
              style={{ marginTop: 10 }}
              onChange={onChangeCheckbox}
            >
              Same as current address
            </Checkbox>
          </div>

        </div>

        <div className="customers__actions__section">
          <Button
            disabled={isLoading}
            htmlType="submit"
            className="customers__save__button"
          >
            <span>Save</span>
          </Button>
          <Button
            onClick={() => {
              form.resetFields();
              setPhoto('');
            }}
            className="customers__cancel__button"
          >
            Cancel
          </Button>
        </div>
      </div>
    </Form>
  );
}
